import type { AttachmentMeta, AttachmentRef } from "@procurement-agents/scope-api";
import { useCallback, useRef, useState, type DragEvent, type ReactNode } from "react";

export const ACCEPT = ".pdf,.docx,.xlsx,.txt,.md,.csv";
export const MAX_FILES = 5;
export type Uploader = (file: File, placeholderHint?: string) => Promise<AttachmentMeta>;   // hint: the needed fact a document is for

const kb = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);

/** Paperclip + drop zone + chips. Files upload as soon as they are chosen; the reply carries their ids. */
export function AttachmentPicker({ upload, items, onChange, disabled, children, actions, busyLabel }: { upload: Uploader; items: AttachmentMeta[]; onChange: (items: AttachmentMeta[]) => void; disabled?: boolean; children?: ReactNode; actions?: ReactNode; busyLabel?: (name: string) => string }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const [current, setCurrent] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const add = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const list = Array.from(files).slice(0, Math.max(0, MAX_FILES - items.length));
    if (Array.from(files).length > list.length) setError(`At most ${MAX_FILES} documents per reply.`);
    for (const f of list) {
      setBusy((b) => b + 1);
      setCurrent(f.name);
      try { const meta = await upload(f); onChange([...items, meta]); items = [...items, meta]; }
      catch (e) { setError(`${f.name}: ${(e as Error).message}`); }
      finally { setBusy((b) => b - 1); }
    }
  }, [upload, items, onChange]);
  const onDrop = (e: DragEvent) => { e.preventDefault(); setOver(false); if (!disabled && e.dataTransfer.files.length) void add(e.dataTransfer.files); };
  return (
    <div className={`attach-zone ${over ? "over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
      {children}
      <div className="attach-row">
        <div className="attach-left">
        <button type="button" className="link attach-btn" onClick={() => input.current?.click()} disabled={disabled || items.length >= MAX_FILES} aria-label="Attach a document" title="Attach a document (PDF, Word, Excel, text)">📎 Attach</button>
        <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { if (e.target.files) void add(e.target.files); e.target.value = ""; }} data-testid="attachment-input" />
        {busy > 0 && <span className="muted small" aria-live="polite">{busyLabel ? busyLabel(current) : "Uploading…"}</span>}
        {items.map((a) => (
          <span key={a.attachment_id} className={`chip removable attachment ${a.extracted_ok ? "" : "warn"}`} title={a.note ?? `${a.text_chars.toLocaleString()} characters of text`}>
            {a.name} <span className="muted">· {kb(a.size)}{a.extracted_ok ? "" : " · no readable text"}</span>
            <button type="button" aria-label={`Remove ${a.name}`} onClick={() => onChange(items.filter((x) => x.attachment_id !== a.attachment_id))}>×</button>
          </span>
        ))}
        {error && <span className="error small">{error}</span>}
        </div>
        {actions && <div className="attach-actions">{actions}</div>}
      </div>
    </div>
  );
}

/** Chips on a transcript turn. Clicking downloads through the API with the bearer token. */
export function TurnAttachments({ items, onOpen }: { items?: AttachmentRef[]; onOpen?: (a: AttachmentRef) => void }) {
  if (!items?.length) return null;
  return (
    <span className="turn-attachments">
      {items.map((a) => <button key={a.attachment_id} type="button" className="chip attachment" onClick={() => onOpen?.(a)} title="Open">📎 {a.name}</button>)}
    </span>
  );
}
